import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, ArrowRight, RefreshCcw, Rocket, Loader2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import api from '@/api/api';
import { toast } from 'sonner';

export default function SetupWizard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadWizard = async () => {
    try {
      setLoading(true);
      const [res, packRes] = await Promise.all([
        api.get('/fleets/setup-wizard'),
        api.get('/contracts/template-packs')
      ]);
      if (res.data?.success) setData(res.data.data);
      if (packRes.data?.success) setPacks(packRes.data?.data || []);
    } catch (error) {
      toast.error('Erro ao carregar setup wizard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWizard();
  }, []);

  if (loading) {
    return (
      <div className="p-12 bg-card rounded-xl border text-center">
        <p className="text-sm font-medium text-muted-foreground">Carregando setup wizard...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-12 bg-card rounded-xl border text-center">
        <p className="text-sm font-medium text-destructive">Não foi possível carregar o wizard.</p>
      </div>
    );
  }

  const { checklist, quick_links } = data;

  const applyPack = async (packId) => {
    try {
      setBusy(true);
      const res = await api.post('/contracts/template-packs/apply', { pack_id: packId });
      toast.success(res.data?.message || 'Pack aplicado com sucesso.');
      await loadWizard();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Falha ao aplicar pack.');
    } finally {
      setBusy(false);
    }
  };

  const startTrial = async () => {
    try {
      setBusy(true);
      await api.post('/billing/start-trial', { plan_type: 'pro', days: 14 });
      toast.success('Trial Pro iniciado.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Falha ao iniciar trial.');
    } finally {
      setBusy(false);
    }
  };

  const getQuickLink = (itemId) => {
    if (!quick_links) return '/';
    switch (itemId) {
      case 'fleet_profile': return quick_links.profile;
      case 'fleet_documents': return quick_links.fleet_documents;
      case 'active_driver': return quick_links.drivers;
      case 'active_vehicle': return quick_links.vehicles;
      default: return quick_links.templates;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest font-semibold text-primary">Onboarding</p>
          <h1 className="text-2xl font-bold tracking-tight">Setup Wizard da Frota</h1>
          <p className="text-sm text-muted-foreground mt-1">Complete os pré-requisitos para operar sem bloqueios no financeiro e compliance.</p>
        </div>
        <Button onClick={loadWizard} variant="outline">
          <RefreshCcw size={14} className="mr-2" /> Atualizar
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground">Progresso do Setup</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Progresso</span>
            <span className="text-sm font-bold">
              {checklist.progress.completed}/{checklist.progress.total} ({checklist.progress.percentage}%)
            </span>
          </div>
          <div className="h-3 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${checklist.progress.percentage}%` }} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {checklist.items.map((item) => (
              <div key={item.id} className="p-6 flex items-start justify-between gap-6">
                <div className="flex items-start gap-3">
                  {item.done ? (
                    <CheckCircle2 className="text-emerald-500 mt-0.5" size={18} />
                  ) : (
                    <Circle className="text-muted-foreground mt-0.5" size={18} />
                  )}
                  <div>
                    <p className="font-semibold">{item.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{item.details}</p>
                  </div>
                </div>

                {!item.done && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(getQuickLink(item.id))}
                  >
                    Corrigir <ArrowRight size={14} className="ml-2" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-sm font-medium text-muted-foreground">Template Packs</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Aplique um pack padrão para acelerar onboarding comercial.</p>
            </div>
            <Button
              disabled={busy}
              onClick={startTrial}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />}
              <span className="ml-2">Iniciar Trial Pro</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {packs.map((pack) => (
              <div key={pack.id} className="border rounded-xl p-4 bg-muted/30">
                <p className="text-sm font-bold">{pack.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{pack.description}</p>
                <Button
                  disabled={busy}
                  onClick={() => applyPack(pack.id)}
                  className="mt-3 w-full"
                  size="sm"
                >
                  Aplicar Pack
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Info size={16} />
            Guia rápido de onboarding
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
            <li>Complete perfil e documentos da frota.</li>
            <li>Cadastre motorista e viatura ativos.</li>
            <li>Aplique um template pack antes do primeiro contrato.</li>
            <li>Ative trial/produto e valide checklist Go-Live.</li>
          </ul>
        </CardContent>
      </Card>

      {checklist.is_ready && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-center gap-3">
          <Rocket className="text-emerald-600" size={18} />
          <p className="text-sm font-semibold text-emerald-700">Setup concluído. A frota está pronta para Go-Live.</p>
        </div>
      )}
    </div>
  );
}
